import {makeAutoObservable} from 'mobx'
import {Lang} from 'yougile-lang'
import {Time} from 'yougile-util'
import {Hub} from 'yougile-model'
import {Sync} from '@client/sync'
import {crmStore} from '@components/crm/crm-store'
import {Board} from '@extensions/board'

export interface FunnelStage {
  stage: string
  count: number
  percent: number
  transitionPercent?: number
}

export class CrmAnalyticsStore {
  funnelStats: FunnelStage[] = []
  totalCount = 0
  succeededCount = 0
  rejectedCount = 0
  dateFilter?: [number, number] = undefined
  board: Hub | undefined = undefined

  constructor() {
    makeAutoObservable(
      this,
      {
        dateFilter: true
      },
      {
        autoBind: true
      }
    )
  }

  get columns(): Hub[] {
    if (!this.board) {return []}
    return Board.listColumns(this.board, {sorted: true, noMirrors: true})
  }

  get boardTasks(): Hub[] {
    if (!this.board) {
      return []
    }
    const box = Sync.get(this.board)
    const result: Hub[] = []
    box.walkForward(this.board, (obj, level) => {
      if (level === 2 && !obj.deleted && !obj.title.startsWith('.')) {
        result.push(obj)
        return false
      }
      return true
    })
    return result
  }

  get succeedPercent(): number {
    return this.totalCount > 0
      ? Math.round((this.succeededCount / this.totalCount) * 100)
      : 0
  }

  setBoard(board: Hub): void {
    this.board = board
    this.syncTasksCount()
  }

  syncTasksCount(): void {
    const board = this.board
    if (!board) {return}

    const cols = this.columns
    const [start, stop] = Array.isArray(this.dateFilter) ? this.dateFilter : []

    const succeededCol = cols.find(col => crmStore.isSucceededColumn(col))
    const rejectedCol = cols.find(col => crmStore.isRejectedColumn(col))

    const otherColumns = cols.filter(
      col => col !== succeededCol && col !== rejectedCol
    )

    const range = [
      ...otherColumns,
      ...(succeededCol ? [succeededCol] : []),
      ...(rejectedCol ? [rejectedCol] : [])
    ]

    const colIds = range.map(col => col.id)
    const counts: {[key: string]: number} = {}
    for (const col of colIds) {
      counts[col] = 0
    }

    const box = Sync.get(board)
    const tasks = this.boardTasks.filter(task => {
      const ts = task.timestamp
      return ts && (!start || ts >= start) && (!stop || ts < stop)
    })

    for (const task of tasks) {
      const parents = box.getAllParentHubs(task)
      const currentCol = parents.find(p => counts[p.id] !== undefined)
      if (!currentCol) {continue}

      const isRejected = crmStore.isRejectedColumn(currentCol)
      if (isRejected) {
        counts[currentCol.id]++
        const history = task.locHistory
        let lastColId: string | undefined
        let maxTs = 0

        for (const [colId, timestamps] of Object.entries(history)) {
          if (counts[colId] === undefined || colId === currentCol.id) {
            continue
          }

          const ts = timestamps[timestamps.length - 1] ?? 0
          if (ts > maxTs) {
            maxTs = ts
            lastColId = colId
          }
        }

        if (lastColId) {
          counts[lastColId] = (counts[lastColId] ?? 0) + 1
        }

        continue
      }

      counts[currentCol.id]++
    }

    this.totalCount = Object.values(counts).reduce((sum, v) => sum + v, 0)
    this.succeededCount = succeededCol ? counts[succeededCol.id] || 0 : 0
    this.rejectedCount = rejectedCol ? counts[rejectedCol.id] || 0 : 0

    const calcCounts: {[key: string]: number} = {}
    for (const [i, col] of otherColumns.entries()) {
      const slice = otherColumns.slice(i)
      calcCounts[col.id] = slice.reduce(
        (sum, col) => sum + (counts[col.id] || 0),
        0
      )
    }

    const stats: FunnelStage[] = []
    let prevCalc = 0

    for (const col of otherColumns) {
      const calc = (calcCounts[col.id] || 0) + this.succeededCount
      const percent =
        this.totalCount > 0 ? Math.round((calc / this.totalCount) * 100) : 0
      const transitionPercent =
        prevCalc > 0 ? Math.round((calc / prevCalc) * 100) : 0

      prevCalc = calc

      stats.push({
        stage: col.title,
        count: calc,
        percent,
        transitionPercent
      })
    }

    if (succeededCol) {
      const count = counts[succeededCol.id] || 0
      stats.push({
        stage: succeededCol.title,
        count,
        percent:
          this.totalCount > 0 ? Math.round((count / this.totalCount) * 100) : 0,
        transitionPercent:
          prevCalc > 0 ? Math.round((count / prevCalc) * 100) : 0
      })
    }

    if (rejectedCol) {
      const count = counts[rejectedCol.id] || 0
      stats.push({
        stage: rejectedCol.title,
        count,
        percent:
          this.totalCount > 0 ? Math.round((count / this.totalCount) * 100) : 0,
        transitionPercent: undefined
      })
    }
    this.funnelStats = stats
  }

  setDateFilter(filter?: [number, number]): void {
    this.dateFilter = filter
    this.syncTasksCount()
  }

  getDateFilterTitle(): string {
    const [start, end] = this.dateFilter ?? []

    if (!start || !end) {
      return Lang.msg.crm.analytics.dealCreatedAtStickerTitle
    }

    const formattedStart = Time.formatDate(start)
    const formattedEnd = Time.formatDate(end)

    return formattedStart === formattedEnd
      ? formattedStart
      : `${formattedStart} — ${formattedEnd}`
  }
}
